import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Unit } from '../../../../app/database/models';
import { DeviceType } from '../../../../app/services/devices-manager.service';
import { MapService } from '../../../../app/services/map.service';
import { DevicesManagerService } from '../../../services/devices-manager.service';
import { TreeComponent } from '../../tree/tree.component';
import { TreeSelectComponent } from '../../tree-select/tree-select.component';
import { ChipsFilterComponent } from '../chip-filter/chips-filter.component';

@Component({
  selector: 'device-filter',
  imports: [
    ChipsFilterComponent,
    TreeComponent,
    TreeSelectComponent,
    MatIcon,
    TranslatePipe,
    MatTooltipModule,
    ReactiveFormsModule,
    MatBadgeModule,
    MatCheckboxModule,
    MatChipsModule,
  ],
  templateUrl: './device-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './device-filter.component.scss',
})
export class DeviceFilterComponent implements OnInit, OnDestroy {
  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this._persistFilters();
  }

  @Input() filtersFormGroup: FormGroup<{
    typeFilter: FormControl<DeviceType[]>;
    statusFilter: FormControl<('moving' | 'stopped' | 'fullyStopped' | 'inactive')[]>;
    unitsFilter: FormControl<Unit[]>;
  }>;
  @Input() $displayFilters: WritableSignal<boolean>;
  @Input() showCloseIcon = true;
  @Input() closeFilterOnReset = true;
  @Input() selectMode = true;

  private _translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private _devicesManagerService = inject(DevicesManagerService);
  private _mapService = inject(MapService);
  public $hideStatusFilter = signal(false);
  public $filtersCount = signal(0);

  public readonly STATUS_ITEMS = [
    { label: this._translate.instant('DEVICE.STATUS.MOVE'), value: 'moving', color: 'secondary' },
    { label: this._translate.instant('DEVICE.STATUS.STOP'), value: 'stopped', color: 'warning' },
    { label: this._translate.instant('DEVICE.STATUS.FULLY_STOPPED'), value: 'fullyStopped', color: 'error' },
    { label: this._translate.instant('DEVICE.STATUS.INACTIVE'), value: 'inactive', color: 'muted' },
  ];

  public readonly TYPE_ITEMS = [
    { label: this._translate.instant('DEVICE.TYPE.BEACON'), value: 'beacon' },
    { label: this._translate.instant('DEVICE.TYPE.DEVICE'), value: 'device' },
  ];

  ngOnInit(): void {
    this.filtersFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._handleFilters();
    });
    this._handleFilters();
  }
  private _handleFilters() {
    const isBeaconOnly =
      this.filtersFormGroup.value.typeFilter.length === 1 &&
      this.filtersFormGroup.value.typeFilter[0] === 'beacon';
    this.$hideStatusFilter.set(isBeaconOnly);

    const unitUids = (this.filtersFormGroup.value.unitsFilter || []).map(({ uid }) => uid);
    // Keep the same reference when the uids did not change, to avoid retriggering the tree's ngOnChanges
    if (JSON.stringify(unitUids) !== JSON.stringify(this.selectedUnitUids)) {
      this.selectedUnitUids = unitUids;
    }

    this._updateFiltersCount();
  }

  public resetFilters() {
    if (this.$filtersCount() === 0) return;

    this.$filtersCount.set(0);
    this.filtersFormGroup.patchValue(
      { statusFilter: [], unitsFilter: [], typeFilter: [] },
      { emitEvent: true }
    );

    if (this.closeFilterOnReset) this.$displayFilters.set(false);
  }

  public toggleFilters() {
    this.$displayFilters.update(v => !v);
  }

  public isChecked(controlName: 'typeFilter' | 'statusFilter', value: string): boolean {
    return ((this.filtersFormGroup.controls[controlName].value as string[]) || []).includes(value);
  }

  public toggleCheckbox(controlName: 'typeFilter' | 'statusFilter', value: string) {
    const control = this.filtersFormGroup.controls[controlName] as unknown as FormControl<string[]>;
    const currentValue = control.value || [];
    control.setValue(
      currentValue.includes(value) ? currentValue.filter(v => v !== value) : [...currentValue, value]
    );
  }

  /**
   * Stable array reference, only recomputed on real form changes: the `tree`
   * clears its selection in `ngOnChanges` whenever its `selected` input
   * reference changes, so a getter (new array every CD cycle) would wipe the
   * user's selection before the tree emits it.
   */
  public selectedUnitUids: number[] = [];

  public onSelectTreeUnits(units: Unit[]) {
    this.filtersFormGroup.controls.unitsFilter.setValue([...units]);
  }

  private _persistFilters() {
    const currentUnitsFilterIds = this._devicesManagerService.unitsToHide(
      this.filtersFormGroup.value.unitsFilter.map(({ uid }) => uid)
    );

    this._mapService.setPreferences([
      { data: this._mapService.$devicesTracking(), item: 'devicesTracking' },
      { data: currentUnitsFilterIds, item: 'deviceFilter' },
      {
        data: this.filtersFormGroup.value.statusFilter,
        item: 'deviceMovingStatusFilter',
      },
      {
        data: this.filtersFormGroup.value.typeFilter,
        item: 'deviceTypeFilter',
      },
    ]);
  }

  private _updateFiltersCount() {
    const filtersCount = Object.values(this.filtersFormGroup.value).filter(
      value => Array.isArray(value) && value.length > 0
    ).length;
    this.$filtersCount.set(filtersCount);
  }

  ngOnDestroy(): void {
    this._persistFilters();
  }
}
